/**
 * TourOptima Chrome extension adds ?cp_embed=1 to the dashboard URL when /chat is embedded in the
 * side-panel iframe. Used to suppress distracting UI (toasts, sounds, Web Notification permission).
 */
export function isDashboardChromeEmbed() {
    if (typeof window === 'undefined') return false;
    try {
        return new URLSearchParams(window.location.search).get('cp_embed') === '1';
    } catch (e) {
        return false;
    }
}

/**
 * True when the dashboard is running inside the extension's side panel — the one surface
 * where a script is allowed to drive the chat search. `cp_embed=1` is the extension's own
 * claim and can be lost across the Auth0 redirect; being framed is a fact about the page,
 * and the side panel is the only thing that frames the dashboard. Either is accepted so the
 * embed keeps working; a plain top-level tab satisfies neither.
 */
export function isEmbeddedInChromeSidePanel() {
    if (typeof window === 'undefined') return false;
    if (isDashboardChromeEmbed()) return true;
    try {
        return window.self !== window.top;
    } catch (e) {
        return false;
    }
}

/**
 * True for an input event no person produced, into a field nobody is in, on a page that is
 * not the side-panel embed.
 *
 * `isTrusted` is false only for events a script dispatched itself — which is exactly how an
 * extension content script "types" into a field: set the value, dispatch `input`. Typing,
 * paste, drag-drop, autofill and the clear X all arrive as trusted events. Inside the side
 * panel that scripted input is the feature (the embedded chat follows the Ventrata order the
 * agent is viewing); in an ordinary tab it is something driving our search from outside.
 *
 * Focus tells the two kinds of script apart. A tool acting *for* the user — a text expander,
 * extension dictation — writes into the field the user is in. The Ventrata extension writes
 * into a field nobody is in: it never focuses the input. So a scripted write into the focused
 * field is the user's own tooling and is left alone; only the unattended one is refused.
 */
export function isScriptedInputOutsideEmbed(event) {
    const native = event?.nativeEvent ?? event;
    if (native?.isTrusted !== false || isEmbeddedInChromeSidePanel()) return false;
    const target = event?.target ?? native?.target;
    if (target && typeof document !== 'undefined' && document.activeElement === target) return false;
    return true;
}
